import { useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { AppUser } from "../Models/AppUser";
import httpService from "../services/httpService";
import { oidcUserManager } from "../auth/userManager";
import { Constants } from "../constants/appConstants";

interface DecodedExp {
  exp: number;
}

const ACTIVE_USER_RENEW_BEFORE_EXP_SECONDS = 5 * 60;
const EMERGENCY_RENEW_BEFORE_EXP_SECONDS = 90;
const FORCE_WARNING_BEFORE_EXP_SECONDS = 75;
const ACTIVE_WINDOW_MS = 2 * 60 * 1000;
const MIN_RENEW_GAP_MS = 15 * 1000;
const CHECK_INTERVAL_MS = 5 * 1000;
const MIN_SECONDS_LEFT_TO_ACCEPT_SAME_TOKEN = 20;
const SHARED_LAST_ACTIVITY_KEY = "lastUserActivityAt";
const SAME_TOKEN_RENEW_SUPPRESS_MS = 45 * 1000;
const SAME_TOKEN_RETRY_DELAY_MS = 1500;
const SAME_TOKEN_MAX_RETRIES = 2;
const WARNING_EVENT_THROTTLE_MS = 15000;
const EXPIRED_EVENT_THROTTLE_MS = 10000;
const RENEW_BLOCK_UNTIL_KEY = "siteminderRenewBlockedUntil";
const RENEW_BLOCK_REASON_KEY = "siteminderRenewBlockReason";

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  "keydown",
  "click",
  "pointerdown",
  "mousedown",
  "touchstart",
  "input",
  "change",
  "focusin",
];

const normalizeStringValue = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }

  return String(value).trim().replace(/^['"]+|['"]+$/g, "");
};

export const useUserActivityTokenRefresh = (
  accessTokenExp: number | null,
  setAppUser: (user: AppUser | null) => void,
  setAccessTokenExp: (exp: number) => void
) => {
  const accessTokenExpRef = useRef<number | null>(accessTokenExp);
  const renewInProgressRef = useRef<boolean>(false);
  const lastRenewAtRef = useRef<number>(0);
  const lastActivityAtRef = useRef<number>(Date.now());
  const lastAcceptedSameTokenExpRef = useRef<number | null>(null);
  const lastAcceptedSameTokenSuppressUntilRef = useRef<number | null>(null);
  const lastWarningDispatchAtRef = useRef<number>(0);
  const lastExpiredDispatchAtRef = useRef<number>(0);

  useEffect(() => {
    accessTokenExpRef.current = accessTokenExp;

    if (
      lastAcceptedSameTokenExpRef.current &&
      accessTokenExp &&
      lastAcceptedSameTokenExpRef.current !== accessTokenExp
    ) {
      console.log(
        "[ActivityTracker] New token expiry detected. Clearing same-token renew marker.",
        {
          previousAcceptedSameExp: lastAcceptedSameTokenExpRef.current,
          newExp: accessTokenExp,
        }
      );

      lastAcceptedSameTokenExpRef.current = null;
      lastAcceptedSameTokenSuppressUntilRef.current = null;
    }
  }, [accessTokenExp]);

  useEffect(() => {
    const readSharedLastActivityAt = (): number | null => {
      try {
        const raw = sessionStorage.getItem(SHARED_LAST_ACTIVITY_KEY);

        if (!raw) {
          return null;
        }

        const parsed = Number(raw);

        return Number.isFinite(parsed) ? parsed : null;
      } catch (err) {
        console.warn(
          "[ActivityTracker] Failed to read shared last activity timestamp.",
          err
        );

        return null;
      }
    };

    const writeSharedLastActivityAt = (ts: number) => {
      try {
        sessionStorage.setItem(
          SHARED_LAST_ACTIVITY_KEY,
          String(ts)
        );
      } catch (err) {
        console.warn(
          "[ActivityTracker] Failed to write shared last activity timestamp.",
          err
        );
      }
    };

    const readRenewBlockUntil = (): number | null => {
      try {
        const raw = sessionStorage.getItem(
          RENEW_BLOCK_UNTIL_KEY
        );

        if (!raw) {
          return null;
        }

        const parsed = Number(raw);

        return Number.isFinite(parsed)
          ? parsed
          : null;
      } catch (err) {
        console.warn(
          "[ActivityTracker] Failed to read renew block timestamp.",
          err
        );

        return null;
      }
    };

    const writeRenewBlock = (
      untilMs: number,
      reason: string
    ) => {
      try {
        sessionStorage.setItem(
          RENEW_BLOCK_UNTIL_KEY,
          String(untilMs)
        );

        sessionStorage.setItem(
          RENEW_BLOCK_REASON_KEY,
          reason
        );

        console.warn(
          "[ActivityTracker] Background renew temporarily blocked.",
          {
            untilMs,
            untilTime:
              new Date(untilMs).toLocaleString(),
            reason,
          }
        );
      } catch (err) {
        console.warn(
          "[ActivityTracker] Failed to write renew block state.",
          err
        );
      }
    };

    const clearRenewBlock = () => {
      try {
        sessionStorage.removeItem(
          RENEW_BLOCK_UNTIL_KEY
        );

        sessionStorage.removeItem(
          RENEW_BLOCK_REASON_KEY
        );
      } catch (err) {
        console.warn(
          "[ActivityTracker] Failed to clear renew block state.",
          err
        );
      }
    };

    const dispatchTokenWarningRequired = (
      secondsLeft: number | null,
      reason: string
    ) => {
      const now = Date.now();

      if (
        now - lastWarningDispatchAtRef.current <
        WARNING_EVENT_THROTTLE_MS
      ) {
        return;
      }

      lastWarningDispatchAtRef.current = now;

      console.warn(
        "[ActivityTracker] Dispatching token warning required event.",
        {
          secondsLeft,
          reason,
        }
      );

      window.dispatchEvent(
        new CustomEvent(
          "auth:token-warning-required",
          {
            detail: {
              secondsLeft,
              reason,
            },
          }
        )
      );
    };

    const dispatchTokenExpired = (
      reason: string
    ) => {
      const now = Date.now();

      if (
        now - lastExpiredDispatchAtRef.current <
        EXPIRED_EVENT_THROTTLE_MS
      ) {
        return;
      }

      lastExpiredDispatchAtRef.current = now;

      console.error(
        "[ActivityTracker] Dispatching token expired event.",
        {
          reason,
        }
      );

      window.dispatchEvent(
        new CustomEvent(
          "auth:token-expired",
          {
            detail: {
              reason,
            },
          }
        )
      );
    };

    const getEffectiveLastActivityAt =
      (): number => {
        const shared =
          readSharedLastActivityAt();

        if (
          shared &&
          shared > lastActivityAtRef.current
        ) {
          lastActivityAtRef.current = shared;
        }

        return lastActivityAtRef.current;
      };

    const hasUsableSession = (): boolean => {
      const rawAuthProvider =
        sessionStorage.getItem(
          "authProvider"
        ) ||
        import.meta.env.VITE_AUTH_PROVIDER ||
        "";

      const normalizedAuthProvider =
        normalizeStringValue(
          rawAuthProvider
        ).toLowerCase();

      const rawAuthenticated =
        sessionStorage.getItem(
          "authenticated"
        );

      const normalizedAuthenticated =
        normalizeStringValue(
          rawAuthenticated
        ).toLowerCase();

      const accessToken =
        sessionStorage.getItem(
          "access_token"
        );

      const valid =
        normalizedAuthProvider ===
          "siteminder" &&
        normalizedAuthenticated ===
          "true" &&
        !!accessToken;

      if (!valid) {
        console.log(
          "[ActivityTracker] Session not ready. Skipping renew check.",
          {
            rawAuthProvider,
            normalizedAuthProvider,
            rawAuthenticated,
            normalizedAuthenticated,
            hasAccessToken:
              !!accessToken,
          }
        );
      }

      return valid;
    };

    const isWarningDialogOpen =
      (): boolean => {
        const dialog =
          document.querySelector(
            ".inactivity-warning-dialog"
          );

        const mask =
          document.querySelector(
            ".p-dialog-mask"
          );

        return !!dialog || !!mask;
      };

    const getRealSecondsLeft =
      (): number | null => {
        const exp =
          accessTokenExpRef.current;

        if (!exp) {
          console.log(
            "[ActivityTracker] accessTokenExp not ready yet."
          );

          return null;
        }

        const now =
          Math.floor(
            Date.now() / 1000
          );

        return exp - now;
      };

    const getRealSecondsLeftFromExp = (
      exp: number | null
    ): number | null => {
      if (!exp) {
        return null;
      }

      const now =
        Math.floor(
          Date.now() / 1000
        );

      return exp - now;
    };

    const isUserRecentlyActive =
      (): boolean => {
        const effectiveLastActivityAt =
          getEffectiveLastActivityAt();

        const idleMs =
          Date.now() -
          effectiveLastActivityAt;

        const recentlyActive =
          idleMs <= ACTIVE_WINDOW_MS;

        console.log(
          "[ActivityTracker] Active window check",
          {
            idleMs,
            activeWindowMs:
              ACTIVE_WINDOW_MS,
            recentlyActive,
            effectiveLastActivityAt:
              new Date(
                effectiveLastActivityAt
              ).toLocaleString(),
          }
        );

        return recentlyActive;
      };

    const buildAppUser = (
      user: any
    ): AppUser => {
      const refreshedUser: AppUser = {
        name: user.profile?.name,
        email: user.profile?.email,
        phone:
          user.profile?.phone as string,
        groups: user.profile?.role
          ? Array.isArray(
              user.profile.role
            )
            ? user.profile.role
            : [user.profile.role]
          : [],
        idToken: user.id_token,
        accessToken:
          user.access_token,
      };

      const appRoleNames = [
        Constants.ADMIN_GROUP_NAME?.toLowerCase(),
        Constants.SUPER_ADMIN_GROUP_NAME?.toLowerCase(),
        Constants.FINAL_REVIEWER_GROUP_NAME?.toLowerCase(),
        Constants.SME_REVIEWER_GROUP_NAME?.toLowerCase(),
        Constants.INITIAL_REVIEWER_GROUP_NAME?.toLowerCase(),
      ];

      const refreshedGroups =
        refreshedUser.groups || [];

      const refreshedHasAppRole =
        refreshedGroups.some(
          (group: string) =>
            appRoleNames.includes(
              group?.toLowerCase()
            )
        );

      if (refreshedHasAppRole) {
        return refreshedUser;
      }

      try {
        const storedUserRaw =
          sessionStorage.getItem(
            "appUser"
          );

        const storedUser =
          storedUserRaw
            ? (JSON.parse(
                storedUserRaw
              ) as AppUser)
            : null;

        const existingGroups =
          storedUser?.groups || [];

        const existingHasAppRole =
          existingGroups.some(
            (group: string) =>
              appRoleNames.includes(
                group?.toLowerCase()
              )
          );

        if (!existingHasAppRole) {
          return refreshedUser;
        }

        return {
          ...refreshedUser,
          groups: existingGroups,
        };
      } catch (error) {
        console.warn(
          "[ActivityTracker] Failed to preserve roles after background session refresh.",
          error
        );

        return refreshedUser;
      }
    };

    const getRenewErrorText = (
      err: unknown
    ): string => {
      if (err instanceof Error) {
        return err.message || "";
      }

      try {
        return JSON.stringify(err);
      } catch {
        return String(err);
      }
    };

    const isInvalidRefreshTokenError = (
      err: unknown
    ): boolean => {
      const text =
        getRenewErrorText(
          err
        ).toLowerCase();

      return (
        text.includes(
          "invalid_refresh_token"
        ) ||
        text.includes(
          "invalid refresh_token"
        ) ||
        text.includes(
          "invalid_grant"
        )
      );
    };

    const clearSameTokenRenewSuppression =
      () => {
        lastAcceptedSameTokenExpRef.current =
          null;

        lastAcceptedSameTokenSuppressUntilRef.current =
          null;
      };

    const tryRenewOnce = async (
      previousExp: number | null,
      label: string
    ): Promise<{
      user: any | null;
      decodedExp: number | null;
    }> => {
      const user =
        await oidcUserManager.signinSilent();

      if (!user) {
        console.warn(
          `[ActivityTracker] ${label}: signinSilent returned null user`
        );

        return {
          user: null,
          decodedExp: null,
        };
      }

      let decodedExp:
        | number
        | null = null;

      try {
        const decoded:
          DecodedExp =
          jwtDecode(
            user.access_token
          );

        decodedExp =
          decoded.exp;
      } catch (decodeErr) {
        console.error(
          `[ActivityTracker] ${label}: Failed to decode refreshed access token`,
          decodeErr
        );
      }

      console.log(
        `[ActivityTracker] ${label}: token comparison`,
        {
          previousExp,
          previousExpTime:
            previousExp
              ? new Date(
                  previousExp * 1000
                ).toLocaleString()
              : "(none)",
          decodedExp,
          decodedExpTime:
            decodedExp
              ? new Date(
                  decodedExp * 1000
                ).toLocaleString()
              : "(none)",
          movedForward:
            previousExp &&
            decodedExp
              ? decodedExp >
                previousExp
              : null,
        }
      );

      return {
        user,
        decodedExp,
      };
    };

    const renewSession = async (
      reason: string
    ) => {
      if (!hasUsableSession()) {
        return false;
      }

      const previousExp =
        accessTokenExpRef.current;

      const previousSecondsLeft =
        getRealSecondsLeftFromExp(
          previousExp
        );

      const isEmergencyRenew =
        previousSecondsLeft !==
          null &&
        previousSecondsLeft <=
          EMERGENCY_RENEW_BEFORE_EXP_SECONDS;

      if (
        isWarningDialogOpen() &&
        !isEmergencyRenew
      ) {
        console.log(
          "[ActivityTracker] Warning dialog open. Skipping normal auto renew.",
          {
            reason,
            previousSecondsLeft,
          }
        );

        return false;
      }

      if (
        !isEmergencyRenew &&
        !isUserRecentlyActive()
      ) {
        console.log(
          "[ActivityTracker] User is not recently active. Skipping auto renew.",
          {
            reason,
          }
        );

        return false;
      }

      if (
        renewInProgressRef.current
      ) {
        console.log(
          "[ActivityTracker] Renew already in progress. Skipping.",
          {
            reason,
          }
        );

        return false;
      }

      const renewBlockUntil =
        readRenewBlockUntil();

      if (
        renewBlockUntil &&
        Date.now() <
          renewBlockUntil
      ) {
        console.warn(
          "[ActivityTracker] Renew is temporarily blocked. Skipping background renew.",
          {
            reason,
            renewBlockUntil,
            renewBlockUntilTime:
              new Date(
                renewBlockUntil
              ).toLocaleString(),
            blockReason:
              sessionStorage.getItem(
                RENEW_BLOCK_REASON_KEY
              ) || "(none)",
          }
        );

        return false;
      }

      if (
        renewBlockUntil &&
        Date.now() >=
          renewBlockUntil
      ) {
        clearRenewBlock();
      }

      const nowMs =
        Date.now();

      if (
        nowMs -
          lastRenewAtRef.current <
        MIN_RENEW_GAP_MS
      ) {
        console.log(
          "[ActivityTracker] Renew throttled. Skipping.",
          {
            reason,
            gapMs:
              nowMs -
              lastRenewAtRef.current,
            minRenewGapMs:
              MIN_RENEW_GAP_MS,
          }
        );

        return false;
      }

      const suppressUntilMs =
        lastAcceptedSameTokenSuppressUntilRef.current;

      if (
        previousExp &&
        lastAcceptedSameTokenExpRef.current ===
          previousExp &&
        previousSecondsLeft !==
          null &&
        previousSecondsLeft >
          MIN_SECONDS_LEFT_TO_ACCEPT_SAME_TOKEN &&
        suppressUntilMs &&
        nowMs < suppressUntilMs
      ) {
        console.log(
          "[ActivityTracker] Same token expiry was accepted recently. Temporarily skipping repeated renew attempt for same token.",
          {
            reason,
            previousExp,
            previousExpTime:
              new Date(
                previousExp *
                  1000
              ).toLocaleString(),
            previousSecondsLeft,
            suppressUntilMs,
            suppressUntilTime:
              new Date(
                suppressUntilMs
              ).toLocaleString(),
          }
        );

        return false;
      }

      if (
        previousExp &&
        lastAcceptedSameTokenExpRef.current ===
          previousExp &&
        suppressUntilMs &&
        nowMs >= suppressUntilMs
      ) {
        console.log(
          "[ActivityTracker] Same-token renew suppression expired. Allowing new renew attempt."
        );

        clearSameTokenRenewSuppression();
      }

      renewInProgressRef.current =
        true;

      console.log(
        "[ActivityTracker] Starting background silent renew.",
        {
          reason,
          isEmergencyRenew,
          previousExp,
          previousExpTime:
            previousExp
              ? new Date(
                  previousExp *
                    1000
                ).toLocaleString()
              : "(none)",
          previousSecondsLeft,
        }
      );

      try {
        let {
          user,
          decodedExp,
        } = await tryRenewOnce(
          previousExp,
          "Silent renew first result"
        );

        let latestSecondsLeft =
          getRealSecondsLeftFromExp(
            decodedExp
          );

        if (
          previousExp &&
          decodedExp &&
          decodedExp <=
            previousExp &&
          (latestSecondsLeft ===
            null ||
            latestSecondsLeft <=
              EMERGENCY_RENEW_BEFORE_EXP_SECONDS)
        ) {
          console.warn(
            "[ActivityTracker] Silent renew returned same/older token near real expiry. Retrying briefly.",
            {
              previousExp,
              previousExpTime:
                new Date(
                  previousExp *
                    1000
                ).toLocaleString(),
              decodedExp,
              decodedExpTime:
                new Date(
                  decodedExp * 1000
                ).toLocaleString(),
              latestSecondsLeft,
              maxRetries:
                SAME_TOKEN_MAX_RETRIES,
            }
          );

          for (
            let attempt = 1;
            attempt <=
            SAME_TOKEN_MAX_RETRIES;
            attempt++
          ) {
            await new Promise<void>(
              (resolve) =>
                window.setTimeout(
                  resolve,
                  SAME_TOKEN_RETRY_DELAY_MS
                )
            );

            const retryResult =
              await tryRenewOnce(
                previousExp,
                `Silent renew retry result ${attempt}`
              );

            if (
              retryResult.user
            ) {
              user =
                retryResult.user;
            }

            if (
              retryResult.decodedExp
            ) {
              decodedExp =
                retryResult.decodedExp;

              latestSecondsLeft =
                getRealSecondsLeftFromExp(
                  decodedExp
                );
            }

            if (
              previousExp &&
              decodedExp &&
              decodedExp >
                previousExp
            ) {
              console.log(
                "[ActivityTracker] Silent renew retry returned a newer token.",
                {
                  attempt,
                  decodedExp,
                  decodedExpTime:
                    new Date(
                      decodedExp *
                        1000
                    ).toLocaleString(),
                  latestSecondsLeft,
                }
              );

              break;
            }
          }
        }

        if (!user) {
          console.warn(
            "[ActivityTracker] signinSilent returned null user"
          );

          return false;
        }

        lastRenewAtRef.current =
          Date.now();

        console.log(
          "[ActivityTracker] signinSilent successful"
        );

        console.log(
          "[ActivityTracker] access_token partial:",
          user.access_token?.slice(
            0,
            30
          ) + "..."
        );

        console.log(
          "[ActivityTracker] id_token partial:",
          user.id_token?.slice(
            0,
            30
          ) + "..."
        );

        console.log(
          "[ActivityTracker] expires_at:",
          user.expires_at
        );

        const appUser =
          buildAppUser(user);

        latestSecondsLeft =
          getRealSecondsLeftFromExp(
            decodedExp
          );

        console.log(
          "[ActivityTracker] Silent renew token comparison:",
          {
            previousExp,
            previousExpTime:
              previousExp
                ? new Date(
                    previousExp *
                      1000
                  ).toLocaleString()
                : "(none)",
            newExp:
              decodedExp,
            newExpTime:
              decodedExp
                ? new Date(
                    decodedExp *
                      1000
                  ).toLocaleString()
                : "(none)",
            movedForward:
              previousExp &&
              decodedExp
                ? decodedExp >
                  previousExp
                : null,
            latestSecondsLeft,
          }
        );

        if (!decodedExp) {
          console.error(
            "[ActivityTracker] Decoded exp is missing after silent renew."
          );

          return false;
        }

        if (
          previousExp &&
          decodedExp <=
            previousExp &&
          (latestSecondsLeft ===
            null ||
            latestSecondsLeft <=
              MIN_SECONDS_LEFT_TO_ACCEPT_SAME_TOKEN)
        ) {
          console.error(
            "[ActivityTracker] Silent renew returned same/older token and real expiry is too close. Ignoring renewed result.",
            {
              previousExp,
              previousExpTime:
                new Date(
                  previousExp *
                    1000
                ).toLocaleString(),
              newExp:
                decodedExp,
              newExpTime:
                new Date(
                  decodedExp *
                    1000
                ).toLocaleString(),
              latestSecondsLeft,
            }
          );

          return false;
        }

        if (
          previousExp &&
          decodedExp <=
            previousExp
        ) {
          lastAcceptedSameTokenExpRef.current =
            decodedExp;

          lastAcceptedSameTokenSuppressUntilRef.current =
            Date.now() +
            SAME_TOKEN_RENEW_SUPPRESS_MS;

          console.warn(
            "[ActivityTracker] Silent renew returned same token exp, but real token is still valid. Temporarily suppressing repeated renew attempts for same token.",
            {
              previousExp,
              previousExpTime:
                new Date(
                  previousExp *
                    1000
                ).toLocaleString(),
              newExp:
                decodedExp,
              newExpTime:
                new Date(
                  decodedExp *
                    1000
                ).toLocaleString(),
              latestSecondsLeft,
              suppressUntilTime:
                new Date(
                  lastAcceptedSameTokenSuppressUntilRef.current
                ).toLocaleString(),
            }
          );
        } else {
          clearSameTokenRenewSuppression();
        }

        try {
          sessionStorage.setItem(
            "access_token",
            user.access_token
          );

          sessionStorage.setItem(
            "id_token",
            user.id_token || ""
          );

          sessionStorage.setItem(
            "appUser",
            JSON.stringify(appUser)
          );

          sessionStorage.setItem(
            "authenticated",
            "true"
          );

          console.log(
            "[ActivityTracker] Stored refreshed OIDC values in sessionStorage"
          );
        } catch (storageErr) {
          console.error(
            "[ActivityTracker] Failed to store refreshed OIDC values",
            storageErr
          );
        }

        try {
          accessTokenExpRef.current =
            decodedExp;

          setAccessTokenExp(
            decodedExp
          );

          sessionStorage.setItem(
            "accessTokenExp",
            String(decodedExp)
          );

          console.log(
            "[ActivityTracker] Updated access token exp:",
            decodedExp
          );
        } catch (expErr) {
          console.error(
            "[ActivityTracker] Failed to update access token exp state",
            expErr
          );
        }

        try {
          console.log(
            "[ActivityTracker] Refreshing backend session using renewed SiteMinder token"
          );

          await httpService.refreshBackendSessionWithToken(
            user.access_token
          );

          console.log(
            "[ActivityTracker] Stored refreshed backend authorization token"
          );
        } catch (backendErr) {
          console.error(
            "[ActivityTracker] Backend session refresh failed",
            backendErr
          );

          return false;
        }

        clearRenewBlock();

        setAppUser(appUser);

        console.log(
          "[ActivityTracker] Background renew flow completed"
        );

        return true;
      } catch (err) {
        console.error(
          "[ActivityTracker] Silent renew failed",
          err
        );

        if (
          isInvalidRefreshTokenError(
            err
          )
        ) {
          const latestSecondsLeft =
            getRealSecondsLeft();

          console.warn(
            "[ActivityTracker] Refresh token is no longer valid. Background renew will be paused temporarily.",
            {
              latestSecondsLeft,
            }
          );

          if (
            accessTokenExpRef.current
          ) {
            lastAcceptedSameTokenExpRef.current =
              accessTokenExpRef.current;

            lastAcceptedSameTokenSuppressUntilRef.current =
              Date.now() +
              SAME_TOKEN_RENEW_SUPPRESS_MS;
          }

          const fallbackSecondsLeft =
            latestSecondsLeft ??
            getRealSecondsLeftFromExp(
              accessTokenExpRef.current
            );

          if (
            fallbackSecondsLeft !==
              null &&
            fallbackSecondsLeft >
              0
          ) {
            const blockUntilMs =
              accessTokenExpRef.current !==
              null
                ? Math.max(
                    Date.now() +
                      30 * 1000,
                    accessTokenExpRef.current *
                      1000 -
                      FORCE_WARNING_BEFORE_EXP_SECONDS *
                        1000
                  )
                : Date.now() +
                  60 * 1000;

            writeRenewBlock(
              blockUntilMs,
              "invalid-refresh-token-background-renew"
            );

            if (
              fallbackSecondsLeft <=
                FORCE_WARNING_BEFORE_EXP_SECONDS &&
              !isWarningDialogOpen()
            ) {
              dispatchTokenWarningRequired(
                fallbackSecondsLeft,
                `${reason} invalid-refresh-token-near-expiry`
              );
            }
          } else {
            dispatchTokenExpired(
              `${reason} invalid-refresh-token-expired`
            );
          }

          lastRenewAtRef.current =
            Date.now();
        }

        return false;
      } finally {
        renewInProgressRef.current =
          false;
      }
    };

    const maybeRenew = async (
      reason: string
    ) => {
      if (!hasUsableSession()) {
        return;
      }

      const secondsLeft =
        getRealSecondsLeft();

      if (secondsLeft === null) {
        return;
      }

      console.log(
        `[ActivityTracker] Check reason=${reason} realSecondsLeft=${secondsLeft}`
      );

      const renewBlockUntil =
        readRenewBlockUntil();

      if (
        renewBlockUntil &&
        Date.now() <
          renewBlockUntil
      ) {
        console.warn(
          "[ActivityTracker] Renew is blocked for now. Skipping automatic renew attempt.",
          {
            reason,
            secondsLeft,
            renewBlockUntil,
            renewBlockUntilTime:
              new Date(
                renewBlockUntil
              ).toLocaleString(),
            blockReason:
              sessionStorage.getItem(
                RENEW_BLOCK_REASON_KEY
              ) || "(none)",
          }
        );

        if (
          secondsLeft <=
            FORCE_WARNING_BEFORE_EXP_SECONDS &&
          !isWarningDialogOpen()
        ) {
          dispatchTokenWarningRequired(
            secondsLeft,
            `${reason} renew-blocked-warning`
          );
        }

        if (secondsLeft <= 0) {
          dispatchTokenExpired(
            `${reason} renew-blocked-expired`
          );
        }

        return;
      }

      if (
        renewBlockUntil &&
        Date.now() >=
          renewBlockUntil
      ) {
        clearRenewBlock();
      }

      if (secondsLeft <= 0) {
        console.warn(
          "[ActivityTracker] Real token expiry already reached. Attempting recovery renew."
        );

        const recovered =
          await renewSession(
            `${reason} expired-recovery`
          );

        const latestAfterRecovery =
          getRealSecondsLeft();

        if (
          !recovered &&
          (latestAfterRecovery ===
            null ||
            latestAfterRecovery <=
              0)
        ) {
          dispatchTokenExpired(
            `${reason} expired-recovery-failed`
          );
        }

        return;
      }

      if (
        secondsLeft <=
          FORCE_WARNING_BEFORE_EXP_SECONDS &&
        !isUserRecentlyActive() &&
        !isWarningDialogOpen()
      ) {
        dispatchTokenWarningRequired(
          secondsLeft,
          `${reason} idle-token-warning`
        );
      }

      if (
        secondsLeft <=
        EMERGENCY_RENEW_BEFORE_EXP_SECONDS
      ) {
        const renewed =
          await renewSession(
            `${reason} emergency-expiring`
          );

        if (!renewed) {
          const latestAfterEmergencyRenew =
            getRealSecondsLeft();

          if (
            latestAfterEmergencyRenew !==
              null &&
            latestAfterEmergencyRenew >
              0 &&
            !isUserRecentlyActive() &&
            !isWarningDialogOpen()
          ) {
            dispatchTokenWarningRequired(
              latestAfterEmergencyRenew,
              `${reason} emergency-renew-failed`
            );
          }

          if (
            latestAfterEmergencyRenew ===
              null ||
            latestAfterEmergencyRenew <=
              0
          ) {
            dispatchTokenExpired(
              `${reason} emergency-renew-failed`
            );
          }
        }

        return;
      }

      if (
        secondsLeft <=
        ACTIVE_USER_RENEW_BEFORE_EXP_SECONDS
      ) {
        await renewSession(
          `${reason} expiring-soon`
        );
      }
    };

    const markActivity = (
      eventType: string
    ) => {
      const now =
        Date.now();

      lastActivityAtRef.current =
        now;

      writeSharedLastActivityAt(
        now
      );

      console.log(
        "[ActivityTracker] Real user activity detected:",
        {
          eventType,
          at: new Date(
            now
          ).toLocaleString(),
        }
      );
    };

    const handleUserActivity = (
      event: Event
    ) => {
      const target =
        event.target;

      if (
        target &&
        target instanceof Element
      ) {
        const dialogElement =
          target.closest(
            ".inactivity-warning-dialog"
          ) ||
          target.closest(
            ".p-dialog"
          ) ||
          target.closest(
            "[role='dialog']"
          ) ||
          target.closest(
            ".p-component-overlay"
          );

        if (dialogElement) {
          console.log(
            "[ActivityTracker] Ignoring dialog interaction:",
            event.type
          );

          return;
        }
      }

      markActivity(
        event.type
      );

      void maybeRenew(
        `activity-${event.type}`
      );
    };

    const interval =
      window.setInterval(
        () => {
          void maybeRenew(
            "timer"
          );
        },
        CHECK_INTERVAL_MS
      );

    console.log(
      "[ActivityTracker] Background renew timer registered",
      {
        checkIntervalMs:
          CHECK_INTERVAL_MS,
        renewBeforeExpSeconds:
          ACTIVE_USER_RENEW_BEFORE_EXP_SECONDS,
        emergencyRenewBeforeExpSeconds:
          EMERGENCY_RENEW_BEFORE_EXP_SECONDS,
        warningBeforeExpSeconds:
          FORCE_WARNING_BEFORE_EXP_SECONDS,
        activeWindowMs:
          ACTIVE_WINDOW_MS,
      }
    );

    ACTIVITY_EVENTS.forEach(
      (eventName) => {
        document.addEventListener(
          eventName,
          handleUserActivity,
          true
        );
      }
    );

    if (
      !readSharedLastActivityAt()
    ) {
      writeSharedLastActivityAt(
        lastActivityAtRef.current
      );
    }

    if (
      hasUsableSession() &&
      accessTokenExpRef.current
    ) {
      void maybeRenew(
        "initial"
      );
    } else {
      console.log(
        "[ActivityTracker] Initial token check skipped. Token/session not ready."
      );
    }

    return () => {
      window.clearInterval(
        interval
      );

      ACTIVITY_EVENTS.forEach(
        (eventName) => {
          document.removeEventListener(
            eventName,
            handleUserActivity,
            true
          );
        }
      );

      console.log(
        "[ActivityTracker] Activity handlers cleared"
      );
    };
  }, [
    setAppUser,
    setAccessTokenExp,
  ]);
};